import { zodResolver } from '@hookform/resolvers/zod';
import { getCurrentUser, signIn, signOut } from 'aws-amplify/auth';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import { Alert, AlertDescription } from '@/components/ui/Alert/Alert';
import { Button } from '@/components/ui/Button/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/Form/Form';
import { Input } from '@/components/ui/Input/Input';
import { PasswordInput } from '@/components/ui/PasswordInput/PasswordInput';
import { paths } from '@/config/paths';
import { captureError } from '@/config/sentry';
import { getStartupConfig } from '@/features/home/api/getStartupConfig';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';
import { useAuthStore } from '@/stores/authStore';

export const LoginForm = () => {
  const { t } = useTranslation('auth', { keyPrefix: 'login' });
  const { t: tValidation } = useTranslation('utils', { keyPrefix: 'validation' });
  const { t: tError } = useTranslation('utils', { keyPrefix: 'error' });
  const { translateError } = useErrorTranslation();

  const navigate = useNavigate();
  const { login } = useAuthStore();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formSchema = z.object({
    username: z
      .string()
      .min(1, { message: tValidation('required') })
      .refine((value) => !value.includes(' '), { message: tValidation('noSpace') }),
    password: z.string().min(1, { message: tValidation('required') }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await signIn({
        username: values.username.toLowerCase(),
        password: values.password,
      });

      if (!response.isSignedIn) {
        const error = new Error(response.nextStep.signInStep);
        error.stack = JSON.stringify({
          username: values.username.toLowerCase(),
          response,
        });
        captureError(error);
        throw error;
      }

      const cognitoUser = await getCurrentUser();
      const startupConfig = await getStartupConfig();

      login({
        cognitoUser,
        center: startupConfig.data.center,
        guardian: startupConfig.data.guardian,
        students: startupConfig.data.students,
        creditCardExpiriesAt: startupConfig.data.creditCardExpiriesAt ? new Date(startupConfig.data.creditCardExpiriesAt) : undefined,
        failedPayment: startupConfig.data.failedPayment,
        overdueInvoice: startupConfig.data.overdueInvoice,
        isDemoEligible: startupConfig.data.isDemoEligible,
        ketchToken: startupConfig.data.ketchToken,
      });

      await navigate(paths.home.path, { replace: true });
    } catch (error) {
      setError(translateError(error, t(`cognitoErrors.${(error as Error)?.message}` as never, tError('GENERAL_ERROR'))));

      // Sign out in case the error occurs after sign in
      await signOut();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      {error && !isLoading && (
        <Alert variant='danger' role='alert' aria-live='assertive'>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <form
        onSubmit={(e) => {
          void form.handleSubmit(onSubmit)(e);
        }}
        className='flex flex-col gap-4'
      >
        <FormField
          control={form.control}
          name='username'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('username.label')}</FormLabel>
              <FormControl>
                <Input {...field} autoComplete='username' />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='password'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('password.label')}</FormLabel>
              <FormControl>
                <PasswordInput {...field} autoComplete='current-password' />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type='submit' isLoading={isLoading} disabled={isLoading} aria-busy={isLoading}>
          {t('submit.label')}
        </Button>
      </form>
    </Form>
  );
};
